import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  Bot, BookOpen, Globe, Smartphone, Database,
  ArrowRight, CheckCircle, Code, ChevronRight, Zap, MessageSquare, Copy, Check,
} from 'lucide-react';

/* ─── reusable code block ─────────────────────────────────────────────────── */
function CodeBlock({ children, language = '' }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    const text = typeof children === 'string' ? children : children?.toString?.() ?? '';
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="rounded-xl overflow-hidden border border-gray-700 my-4">
      <div className="bg-gray-800 px-4 py-1.5 flex items-center justify-between border-b border-gray-700">
        <span className="text-xs text-gray-400 font-mono">{language}</span>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white transition-colors"
        >
          {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
          <span>{copied ? 'Copied!' : 'Copy'}</span>
        </button>
      </div>
      <pre className="bg-gray-900 text-green-300 text-sm font-mono p-5 overflow-x-auto leading-relaxed whitespace-pre">
        {children}
      </pre>
    </div>
  );
}

/* ─── step badge ──────────────────────────────────────────────────────────── */
function Step({ n, text }) {
  return (
    <div className="flex items-start gap-3 py-2">
      <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary-600 text-white text-sm font-bold flex items-center justify-center mt-0.5">
        {n}
      </span>
      <p className="text-gray-700">{text}</p>
    </div>
  );
}

/* ─── section heading ─────────────────────────────────────────────────────── */
function SectionHeading({ icon: Icon, id, title, subtitle }) {
  return (
    <div id={id} className="flex items-start gap-4 mb-6 pt-4">
      <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center flex-shrink-0">
        <Icon className="w-6 h-6 text-primary-600" />
      </div>
      <div>
        <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
        {subtitle && <p className="text-gray-500 mt-1">{subtitle}</p>}
      </div>
    </div>
  );
}

/* ─── page ────────────────────────────────────────────────────────────────── */
const ChatbotDocsPage = () => {
  const { t } = useTranslation();

  const toc = [
    { id: 'create',         label: t('chatbotDocs.toc_create') },
    { id: 'knowledge-base', label: t('chatbotDocs.toc_kb') },
    { id: 'website',        label: t('chatbotDocs.toc_website') },
    { id: 'mobile',         label: t('chatbotDocs.toc_mobile') },
    { id: 'api',            label: t('chatbotDocs.toc_api') },
    { id: 'whatsapp',       label: t('chatbotDocs.toc_whatsapp') },
    { id: 'messenger',      label: t('chatbotDocs.toc_messenger') },
    { id: 'instagram',      label: t('chatbotDocs.toc_instagram') },
  ];

  return (
    <>
      <Helmet>
        <title>Chatbot Integration Guide | Documentation</title>
        <meta name="description" content="Step-by-step guide to create, configure, and embed your AI chatbot on websites and mobile apps." />
      </Helmet>

      <div className="pt-20 min-h-screen bg-white">

        {/* Hero */}
        <section className="section-sm bg-gradient-to-br from-primary-50 to-blue-50">
          <div className="container-custom">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
                <Link to="/docs" className="hover:text-primary-600">{t('chatbotDocs.breadcrumb')}</Link>
                <ChevronRight className="w-4 h-4" />
                <span className="text-gray-900 font-medium">{t('chatbotDocs.title')}</span>
              </div>
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-600 rounded-full mb-5">
                <Bot className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-4xl font-bold text-gray-900 mb-3">{t('chatbotDocs.title')}</h1>
              <p className="text-lg text-gray-600 max-w-2xl">
                {t('chatbotDocs.heroSubtitle')}
              </p>
            </motion.div>
          </div>
        </section>

        {/* Body: ToC sidebar + content */}
        <div className="container-custom py-12">
          <div className="flex flex-col lg:flex-row gap-12">

            {/* Sticky ToC */}
            <aside className="lg:w-56 flex-shrink-0">
              <div className="sticky top-24 bg-gray-50 rounded-xl border border-gray-200 p-5">
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">{t('chatbotDocs.onThisPage')}</p>
                <ul className="space-y-2">
                  {toc.map(item => (
                    <li key={item.id}>
                      <a
                        href={`#${item.id}`}
                        className="text-sm text-gray-600 hover:text-primary-600 flex items-center gap-1.5"
                      >
                        <ArrowRight className="w-3.5 h-3.5 flex-shrink-0" />
                        {item.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>

            {/* Main content */}
            <main className="flex-1 max-w-3xl space-y-16">

              {/* ── 1. Creating a Chatbot ─────────────────────────────────── */}
              <section>
                <SectionHeading
                  icon={Bot}
                  id="create"
                  title={t('chatbotDocs.s1_title')}
                  subtitle={t('chatbotDocs.s1_subtitle')}
                />

                <div className="space-y-1 mb-6">
                  <Step n={1} text={t('chatbotDocs.s1_step1')} />
                  <Step n={2} text={t('chatbotDocs.s1_step2')} />
                  <Step n={3} text={t('chatbotDocs.s1_step3')} />
                </div>

                <div className="bg-gray-50 rounded-xl border border-gray-200 p-5 mb-4">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-500 border-b border-gray-200">
                        <th className="pb-2 font-semibold">{t('chatbotDocs.s1_field')}</th>
                        <th className="pb-2 font-semibold">{t('chatbotDocs.s1_fieldDesc')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {[
                        [t('chatbotDocs.s1_fname'),     t('chatbotDocs.s1_fnameDesc')],
                        [t('chatbotDocs.s1_fBotName'),  t('chatbotDocs.s1_fBotNameDesc')],
                        [t('chatbotDocs.s1_fWelcome'),  t('chatbotDocs.s1_fWelcomeDesc')],
                        [t('chatbotDocs.s1_fPrompt'),   t('chatbotDocs.s1_fPromptDesc')],
                        [t('chatbotDocs.s1_fColor'),    t('chatbotDocs.s1_fColorDesc')],
                        [t('chatbotDocs.s1_fPosition'), t('chatbotDocs.s1_fPositionDesc')],
                      ].map(([f, d]) => (
                        <tr key={f} className="text-gray-700">
                          <td className="py-2 pr-4 font-medium font-mono text-primary-700">{f}</td>
                          <td className="py-2">{d}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="flex items-start gap-2 p-4 bg-blue-50 rounded-xl border border-blue-200 text-sm text-blue-800">
                  <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-600" />
                  <span>{t('chatbotDocs.s1_note')}</span>
                </div>
              </section>

              {/* ── 2. Knowledge Base & RAG ───────────────────────────────── */}
              <section>
                <SectionHeading
                  icon={Database}
                  id="knowledge-base"
                  title={t('chatbotDocs.s2_title')}
                  subtitle={t('chatbotDocs.s2_subtitle')}
                />

                <p className="text-gray-600 mb-5">
                  {t('chatbotDocs.s2_intro')}
                </p>

                <div className="space-y-5">

                  {/* Documents */}
                  <div className="rounded-xl border border-gray-200 p-5">
                    <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                      <BookOpen className="w-4 h-4 text-primary-600" /> {t('chatbotDocs.s2_docs')}
                    </h3>
                    <p className="text-sm text-gray-600 mb-3">
                      {t('chatbotDocs.s2_docsDesc')}
                    </p>
                    <div className="flex items-start gap-2 text-xs text-gray-500">
                      <CheckCircle className="w-3.5 h-3.5 mt-0.5 text-green-500 flex-shrink-0" />
                      {t('chatbotDocs.s2_docsSupported')}
                    </div>
                  </div>

                  {/* Website URLs */}
                  <div className="rounded-xl border border-gray-200 p-5">
                    <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                      <Globe className="w-4 h-4 text-primary-600" /> {t('chatbotDocs.s2_urls')}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {t('chatbotDocs.s2_urlsDesc')}
                    </p>
                  </div>

                  {/* External APIs */}
                  <div className="rounded-xl border border-gray-200 p-5">
                    <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                      <Code className="w-4 h-4 text-primary-600" /> {t('chatbotDocs.s2_apis')}
                    </h3>
                    <p className="text-sm text-gray-600 mb-3">
                      {t('chatbotDocs.s2_apisDesc')}
                    </p>

                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{t('chatbotDocs.s2_configFields')}</p>
                    <div className="bg-gray-50 rounded-lg p-3 text-sm space-y-1.5">
                      {[
                        [t('chatbotDocs.s2_afName'),      t('chatbotDocs.s2_afNameDesc')],
                        [t('chatbotDocs.s2_afDesc'),      t('chatbotDocs.s2_afDescDesc')],
                        [t('chatbotDocs.s2_afEndpoint'),  t('chatbotDocs.s2_afEndpointDesc')],
                        [t('chatbotDocs.s2_afAuthName'),  t('chatbotDocs.s2_afAuthNameDesc')],
                        [t('chatbotDocs.s2_afAuthValue'), t('chatbotDocs.s2_afAuthValueDesc')],
                      ].map(([f, d]) => (
                        <div key={f} className="flex gap-2">
                          <span className="font-mono text-primary-700 font-medium w-40 flex-shrink-0">{f}</span>
                          <span className="text-gray-600">{d}</span>
                        </div>
                      ))}
                    </div>

                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mt-4 mb-1">{t('chatbotDocs.s2_urlExamples')}</p>
                    <CodeBlock language="plaintext">{`# With search placeholder (chatbot sends the relevant search term)
https://api.myshop.com/products?q={query}&limit=5

# Without placeholder (chatbot fetches the full list)
https://api.myshop.com/products

# DummyJSON demo endpoint
https://dummyjson.com/products`}</CodeBlock>

                    <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200 text-xs text-amber-800 mt-3">
                      <CheckCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-amber-600" />
                      {t('chatbotDocs.s2_note')}
                    </div>
                  </div>
                </div>
              </section>

              {/* ── 3. Website Integration ────────────────────────────────── */}
              <section>
                <SectionHeading
                  icon={Globe}
                  id="website"
                  title={t('chatbotDocs.s3_title')}
                  subtitle={t('chatbotDocs.s3_subtitle')}
                />

                <div className="space-y-1 mb-4">
                  <Step n={1} text={t('chatbotDocs.s3_step1')} />
                  <Step n={2} text={t('chatbotDocs.s3_step2')} />
                  <Step n={3} text={t('chatbotDocs.s3_step3')} />
                </div>

                <CodeBlock language="html">{`<!-- Paste before </body> in your HTML page -->
<script
  src="https://your-platform.com/widget.js"
  data-chatbot-id="YOUR_CHATBOT_ID"
  data-api-url="https://your-platform.com"
  data-primary-color="#007bff"
  data-position="bottom-right"
  data-welcome-message="Hello! How can I help you?"
  data-bot-name="Customer Support"
></script>`}</CodeBlock>

                <div className="bg-gray-50 rounded-xl border border-gray-200 p-5 mt-4">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">{t('chatbotDocs.s3_attrRef')}</p>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-500 border-b border-gray-200">
                        <th className="pb-2 font-semibold">{t('chatbotDocs.s3_attrAttr')}</th>
                        <th className="pb-2 font-semibold">{t('chatbotDocs.s3_attrRequired')}</th>
                        <th className="pb-2 font-semibold">{t('chatbotDocs.s3_attrDesc')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 text-gray-700">
                      {[
                        ['data-chatbot-id',      t('chatbotDocs.s3_attr1Req'), t('chatbotDocs.s3_attr1Desc')],
                        ['data-api-url',         t('chatbotDocs.s3_attr2Req'), t('chatbotDocs.s3_attr2Desc')],
                        ['data-primary-color',   t('chatbotDocs.s3_attr3Req'), t('chatbotDocs.s3_attr3Desc')],
                        ['data-position',        t('chatbotDocs.s3_attr4Req'), t('chatbotDocs.s3_attr4Desc')],
                        ['data-welcome-message', t('chatbotDocs.s3_attr5Req'), t('chatbotDocs.s3_attr5Desc')],
                        ['data-bot-name',        t('chatbotDocs.s3_attr6Req'), t('chatbotDocs.s3_attr6Desc')],
                      ].map(([attr, req, desc]) => (
                        <tr key={attr}>
                          <td className="py-2 pr-3 font-mono text-primary-700 text-xs">{attr}</td>
                          <td className="py-2 pr-3 text-xs">{req}</td>
                          <td className="py-2 text-xs">{desc}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              {/* ── 4. Mobile App Integration ─────────────────────────────── */}
              <section>
                <SectionHeading
                  icon={Smartphone}
                  id="mobile"
                  title={t('chatbotDocs.s4_title')}
                  subtitle={t('chatbotDocs.s4_subtitle')}
                />

                <div className="space-y-1 mb-4">
                  <Step n={1} text={t('chatbotDocs.s4_step1')} />
                </div>

                <CodeBlock language="bash">{`npx expo install react-native-webview`}</CodeBlock>

                <div className="space-y-1 my-4">
                  <Step n={2} text={t('chatbotDocs.s4_step2')} />
                </div>

                <CodeBlock language="javascript">{`import React, { useState } from 'react';
import {
  View, TouchableOpacity, Text, Modal,
  StyleSheet, SafeAreaView,
} from 'react-native';
import { WebView } from 'react-native-webview';

// ── Replace with your actual platform URL ──
const CHATBOT_BASE = 'https://your-platform.com';

// Injects CSS to make the chat panel fill the full screen height
const INJECTED_JS = \`
(function() {
  var s = document.createElement('style');
  s.textContent = [
    '[style*="max-height: 560px"] {',
    '  max-height: 100vh !important;',
    '  height:     100vh !important;',
    '  bottom:     0     !important;',
    '  left:       0     !important;',
    '  right:      0     !important;',
    '  border-radius: 0  !important;',
    '}',
  ].join('\\\\n');
  document.head.appendChild(s);
})();
true;
\`;

// HTML page that loads the chatbot widget script
const WIDGET_HTML = \`
<!DOCTYPE html>
<html>
<head>
  <meta name="viewport"
    content="width=device-width, initial-scale=1.0, maximum-scale=1.0">
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { background: #f9fafb; height: 100vh; overflow: hidden; }
  </style>
</head>
<body>
  <script
    src="\${CHATBOT_BASE}/widget.js"
    data-chatbot-id="YOUR_CHATBOT_ID"
    data-api-url="\${CHATBOT_BASE}"
    data-primary-color="#007bff"
    data-position="bottom-right"
    data-welcome-message="Hello! How can I help you?"
    data-bot-name="Customer Support"
  ><\\/script>
</body>
</html>
\`;

export default function ChatbotWidget() {
  const [visible, setVisible] = useState(false);

  return (
    <>
      {/* Floating chat button */}
      <TouchableOpacity
        style={styles.fab}
        onPress={() => setVisible(true)}
        activeOpacity={0.85}
      >
        <Text style={{ fontSize: 24 }}>💬</Text>
      </TouchableOpacity>

      {/* Full-screen modal */}
      <Modal
        visible={visible}
        animationType="slide"
        onRequestClose={() => setVisible(false)}
      >
        <SafeAreaView style={{ flex: 1, backgroundColor: '#f9fafb' }}>

          {/* Close button */}
          <TouchableOpacity
            style={styles.closeBtn}
            onPress={() => setVisible(false)}
            activeOpacity={0.75}
          >
            <Text style={styles.closeBtnText}>✕</Text>
          </TouchableOpacity>

          <WebView
            source={{ html: WIDGET_HTML, baseUrl: CHATBOT_BASE }}
            style={{ flex: 1 }}
            javaScriptEnabled
            domStorageEnabled
            originWhitelist={['*']}
            mixedContentMode="always"
            injectedJavaScript={INJECTED_JS}
          />
        </SafeAreaView>
      </Modal>
    </>
  );
}

const styles = StyleSheet.create({
  fab: {
    position: 'absolute',
    bottom: 80,
    right: 20,
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: '#007bff',
    alignItems: 'center',
    justifyContent: 'center',
    elevation: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.3,
    shadowRadius: 5,
    zIndex: 999,
  },
  closeBtn: {
    position: 'absolute',
    top: 12,
    right: 12,
    zIndex: 999,
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: 'rgba(0,0,0,0.35)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeBtnText: {
    color: '#fff',
    fontSize: 15,
    fontWeight: '700',
    lineHeight: 18,
  },
});`}</CodeBlock>

                <div className="space-y-1 mt-4 mb-4">
                  <Step n={3} text={t('chatbotDocs.s4_step3')} />
                </div>

                <CodeBlock language="javascript">{`// App.tsx (or App.js)
import ChatbotWidget from './src/components/ChatbotWidget';

function App() {
  return (
    <SafeAreaProvider>
      {/* ... your existing providers and navigator ... */}
      <AppNavigator />
      {isAuthenticated && <ChatbotWidget />}
    </SafeAreaProvider>
  );
}`}</CodeBlock>

                <div className="mt-5 rounded-xl border border-gray-200 p-5 bg-gray-50">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">{t('chatbotDocs.s4_platformNotes')}</p>
                  <div className="space-y-2 text-sm text-gray-600">
                    {[
                      [t('chatbotDocs.s4_pAndroid'), t('chatbotDocs.s4_pAndroidDesc')],
                      [t('chatbotDocs.s4_pDevice'),  t('chatbotDocs.s4_pDeviceDesc')],
                      [t('chatbotDocs.s4_pIOS'),     t('chatbotDocs.s4_pIOSDesc')],
                      [t('chatbotDocs.s4_pProd'),    t('chatbotDocs.s4_pProdDesc')],
                    ].map(([env, note]) => (
                      <div key={env} className="flex gap-3">
                        <span className="font-semibold w-36 flex-shrink-0 text-gray-800">{env}</span>
                        <span>{note}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              {/* ── 5. REST API Integration ───────────────────────────── */}
              <section>
                <SectionHeading
                  icon={Zap}
                  id="api"
                  title={t('chatbotDocs.s5_title')}
                  subtitle={t('chatbotDocs.s5_subtitle')}
                />

                <p className="text-gray-600 mb-5">
                  {t('chatbotDocs.s5_intro')}
                </p>

                {/* Auth */}
                <h3 className="font-semibold text-gray-900 mb-2">{t('chatbotDocs.s5_auth')}</h3>
                <p className="text-sm text-gray-600 mb-3">
                  {t('chatbotDocs.s5_authDesc')}
                </p>

                {/* Send a message */}
                <h3 className="font-semibold text-gray-900 mb-2 mt-6">{t('chatbotDocs.s5_sendMsg')}</h3>
                <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 mb-3 text-sm font-mono">
                  <span className="text-green-700 font-semibold">POST</span>{' '}
                  <span className="text-gray-800">https://your-platform.com/api/chat</span>
                </div>

                <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 mb-2">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{t('chatbotDocs.s5_reqHeaders')}</p>
                  <table className="w-full text-sm">
                    <tbody className="divide-y divide-gray-100">
                      {[
                        ['X-Widget-Key', 'Widget Key — for embedded widget (browser-safe)'],
                        ['Authorization', 'Bearer YOUR_API_KEY — for server-side integrations (secret)'],
                        ['Content-Type', 'application/json'],
                      ].map(([h, d]) => (
                        <tr key={h}>
                          <td className="py-1.5 pr-4 font-mono text-primary-700 text-xs w-44">{h}</td>
                          <td className="py-1.5 text-gray-600 text-xs">{d}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="text-xs text-gray-400 mt-2">Use one of the two auth headers per request — not both.</p>
                </div>

                <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 mb-4">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{t('chatbotDocs.s5_reqBody')}</p>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-400 border-b border-gray-200 text-xs">
                        <th className="pb-1.5 font-semibold w-36">{t('chatbotDocs.s5_field')}</th>
                        <th className="pb-1.5 font-semibold w-24">{t('chatbotDocs.s5_required')}</th>
                        <th className="pb-1.5 font-semibold">Description</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 text-xs text-gray-700">
                      {[
                        ['sessionId',       '✅ Yes',  t('chatbotDocs.s5_fSessionIdDesc')],
                        ['message',         '✅ Yes',  t('chatbotDocs.s5_fMessageDesc')],
                        ['visitorId',       'Optional', t('chatbotDocs.s5_fVisitorIdDesc')],
                        ['visitorEmail',    'Optional', t('chatbotDocs.s5_fVisitorEmailDesc')],
                        ['visitorName',     'Optional', t('chatbotDocs.s5_fVisitorNameDesc')],
                        ['mediaUrl',        'Optional', t('chatbotDocs.s5_fMediaUrlDesc')],
                        ['channelType',     'Optional', t('chatbotDocs.s5_fChannelTypeDesc')],
                        ['channelSenderId', 'Optional', t('chatbotDocs.s5_fChannelSenderIdDesc')],
                      ].map(([f, r, d]) => (
                        <tr key={f}>
                          <td className="py-1.5 pr-2 font-mono text-primary-700">{f}</td>
                          <td className="py-1.5 pr-2">{r}</td>
                          <td className="py-1.5 text-gray-600">{d}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <h3 className="font-semibold text-gray-900 mb-2">{t('chatbotDocs.s5_codeExamples')}</h3>

                <CodeBlock language="bash">{`# cURL — Widget Key (embedded widget)
curl -X POST https://your-platform.com/api/chat \\
  -H "X-Widget-Key: YOUR_WIDGET_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "sessionId":   "session-abc123",
    "message":     "What products do you offer?",
    "visitorId":   "user-42",
    "channelType": "WIDGET"
  }'

# cURL — API Key (server-side custom integration)
curl -X POST https://your-platform.com/api/chat \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "sessionId": "session-abc123",
    "message":   "What products do you offer?",
    "visitorId": "user-42"
  }'

# cURL — WhatsApp channel (Widget Key)
curl -X POST https://your-platform.com/api/chat \\
  -H "X-Widget-Key: YOUR_WIDGET_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "sessionId":       "session-abc123",
    "message":         "Hello from WhatsApp",
    "visitorId":       "user-42",
    "channelType":     "WHATSAPP",
    "channelSenderId": "971501234567"
  }'`}</CodeBlock>

                <CodeBlock language="javascript">{`// JavaScript (fetch) — Widget Key for browser widget
const response = await fetch('https://your-platform.com/api/chat', {
  method: 'POST',
  headers: {
    'X-Widget-Key': 'YOUR_WIDGET_KEY',
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({
    sessionId:   'session-abc123',
    message:     'What products do you offer?',
    visitorId:   'user-42',
    channelType: 'WIDGET',
  }),
});
const data = await response.json();
console.log(data.message); // AI reply text

// Server-side — API Key (generated in chatbot settings, keep secret)
const response = await fetch('https://your-platform.com/api/chat', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({
    sessionId:    'session-abc123',
    message:      'What products do you offer?',
    visitorId:    'user-42',
    visitorEmail: 'user@example.com',
    visitorName:  'John',
    mediaUrl:     'https://example.com/image.jpg', // optional
  }),
});`}</CodeBlock>

                <CodeBlock language="python">{`# Python (requests) — server-side using API Key
import requests, uuid

response = requests.post(
    'https://your-platform.com/api/chat',
    headers={
        'Authorization': 'Bearer YOUR_API_KEY',
        'Content-Type': 'application/json',
    },
    json={
        'sessionId':       str(uuid.uuid4()),
        'message':         'What products do you offer?',
        'visitorId':       'user-42',
        'visitorEmail':    'user@example.com',
        'visitorName':     'John',
        # optional fields
        'mediaUrl':        None,      # URL of image/audio/document to attach
        'channelType':     'WIDGET',  # 'WIDGET' (default) or 'WHATSAPP'
        'channelSenderId': None,      # wa_id — required for WHATSAPP channel
    }
)

reply = response.json()
print(reply['message'])  # AI reply text`}</CodeBlock>

                {/* Response */}
                <h3 className="font-semibold text-gray-900 mb-2 mt-4">{t('chatbotDocs.s5_response')}</h3>
                <CodeBlock language="json">{`{
  "sessionId": "session-abc123",
  "message":   "We offer two main products: a CMMS for maintenance management and an AI Chatbot platform.",
  "timestamp": "2025-06-01T12:00:00Z"
}`}</CodeBlock>

                {/* End conversation */}
                <h3 className="font-semibold text-gray-900 mb-2 mt-6">{t('chatbotDocs.s5_endConv')}</h3>
                <p className="text-sm text-gray-600 mb-3">
                  {t('chatbotDocs.s5_endConvDesc')}
                </p>
                <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 mb-3 text-sm font-mono">
                  <span className="text-green-700 font-semibold">POST</span>{' '}
                  <span className="text-gray-800">https://your-platform.com/api/chat/<em>{'{'}</em>sessionId<em>{'}'}</em>/end</span>
                </div>
                <CodeBlock language="bash">{`curl -X POST https://your-platform.com/api/chat/session-abc123/end`}</CodeBlock>

                <div className="flex items-start gap-2 p-4 bg-amber-50 rounded-xl border border-amber-200 text-sm text-amber-800 mt-4">
                  <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-600" />
                  <span>{t('chatbotDocs.s5_note')}</span>
                </div>
              </section>

              {/* ── 6. WhatsApp Channel Setup ─────────────────────────── */}
              <section>
                <SectionHeading
                  icon={MessageSquare}
                  id="whatsapp"
                  title={t('chatbotDocs.s6_title')}
                  subtitle={t('chatbotDocs.s6_subtitle')}
                />

                <p className="text-gray-600 mb-5">
                  {t('chatbotDocs.s6_intro')}
                </p>

                {/* Prerequisites */}
                <h3 className="font-semibold text-gray-900 mb-3">{t('chatbotDocs.s6_prereqs')}</h3>
                <div className="rounded-xl border border-gray-200 p-5 mb-6 space-y-2">
                  {[
                    [t('chatbotDocs.s6_p1'), t('chatbotDocs.s6_p1Desc')],
                    [t('chatbotDocs.s6_p2'), t('chatbotDocs.s6_p2Desc')],
                    [t('chatbotDocs.s6_p3'), t('chatbotDocs.s6_p3Desc')],
                    [t('chatbotDocs.s6_p4'), t('chatbotDocs.s6_p4Desc')],
                  ].map(([title, desc]) => (
                    <div key={title} className="flex gap-3 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <div><span className="font-semibold text-gray-800">{title}:</span> <span className="text-gray-600">{desc}</span></div>
                    </div>
                  ))}
                </div>

                {/* Step 1 – Portal */}
                <h3 className="font-semibold text-gray-900 mb-3">{t('chatbotDocs.s6_step1Title')}</h3>
                <div className="space-y-1 mb-4">
                  <Step n={1} text={t('chatbotDocs.s6_step1_1')} />
                  <Step n={2} text={t('chatbotDocs.s6_step1_2')} />
                </div>
                <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 mb-5">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-400 border-b border-gray-200 text-xs">
                        <th className="pb-1.5 font-semibold w-44">{t('chatbotDocs.s6_fField')}</th>
                        <th className="pb-1.5 font-semibold">{t('chatbotDocs.s6_fWhere')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 text-xs text-gray-700">
                      {[
                        [t('chatbotDocs.s6_fDispName'),    t('chatbotDocs.s6_fDispNameDesc')],
                        [t('chatbotDocs.s6_fPhoneId'),     t('chatbotDocs.s6_fPhoneIdDesc')],
                        [t('chatbotDocs.s6_fToken'),       t('chatbotDocs.s6_fTokenDesc')],
                        [t('chatbotDocs.s6_fWebhookToken'),t('chatbotDocs.s6_fWebhookTokenDesc')],
                      ].map(([f, d]) => (
                        <tr key={f}>
                          <td className="py-1.5 pr-2 font-mono text-primary-700">{f}</td>
                          <td className="py-1.5 text-gray-600">{d}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex items-start gap-2 p-4 bg-blue-50 rounded-xl border border-blue-200 text-sm text-blue-800 mb-6">
                  <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-600" />
                  <span>{t('chatbotDocs.s6_step1Note')}</span>
                </div>

                {/* Step 2 – Meta */}
                <h3 className="font-semibold text-gray-900 mb-3">{t('chatbotDocs.s6_step2Title')}</h3>
                <div className="space-y-1 mb-5">
                  <Step n={1} text={t('chatbotDocs.s6_step2_1')} />
                  <Step n={2} text={t('chatbotDocs.s6_step2_2')} />
                  <Step n={3} text={t('chatbotDocs.s6_step2_3')} />
                  <Step n={4} text={t('chatbotDocs.s6_step2_4')} />
                  <Step n={5} text={t('chatbotDocs.s6_step2_5')} />
                </div>

                {/* How it works */}
                <h3 className="font-semibold text-gray-900 mb-3">{t('chatbotDocs.s6_howTitle')}</h3>
                <div className="rounded-xl border border-gray-200 p-5 mb-5 space-y-3 text-sm text-gray-600">
                  {[
                    t('chatbotDocs.s6_how1'),
                    t('chatbotDocs.s6_how2'),
                    t('chatbotDocs.s6_how3'),
                    t('chatbotDocs.s6_how4'),
                    t('chatbotDocs.s6_how5'),
                  ].map((text, i) => (
                    <div key={i} className="flex gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 text-green-700 text-xs font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
                      <p>{text}</p>
                    </div>
                  ))}
                </div>

                {/* Billing note */}
                <div className="flex items-start gap-2 p-4 bg-amber-50 rounded-xl border border-amber-200 text-sm text-amber-800">
                  <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-600" />
                  <span>{t('chatbotDocs.s6_billing')}</span>
                </div>
              </section>

              {/* ── 7. Facebook Messenger Channel Setup ──────────────────── */}
              <section>
                <SectionHeading
                  icon={MessageSquare}
                  id="messenger"
                  title={t('chatbotDocs.s7_title')}
                  subtitle={t('chatbotDocs.s7_subtitle')}
                />

                <p className="text-gray-600 mb-5">
                  {t('chatbotDocs.s7_intro')}
                </p>

                <h3 className="font-semibold text-gray-900 mb-3">{t('chatbotDocs.s7_prereqs')}</h3>
                <div className="rounded-xl border border-gray-200 p-5 mb-6 space-y-2">
                  {[
                    [t('chatbotDocs.s7_p1'), t('chatbotDocs.s7_p1Desc')],
                    [t('chatbotDocs.s7_p2'), t('chatbotDocs.s7_p2Desc')],
                    [t('chatbotDocs.s7_p3'), t('chatbotDocs.s7_p3Desc')],
                    [t('chatbotDocs.s7_p4'), t('chatbotDocs.s7_p4Desc')],
                  ].map(([title, desc]) => (
                    <div key={title} className="flex gap-3 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <div><span className="font-medium text-gray-800">{title}</span> — <span className="text-gray-600">{desc}</span></div>
                    </div>
                  ))}
                </div>

                <h3 className="font-semibold text-gray-900 mb-3">{t('chatbotDocs.s7_step1Title')}</h3>
                <div className="space-y-1 mb-4">
                  <Step n={1} text={t('chatbotDocs.s7_step1_1')} />
                  <Step n={2} text={t('chatbotDocs.s7_step1_2')} />
                </div>
                <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 mb-5">
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      <th className="pb-2 pr-4">{t('chatbotDocs.s7_fField')}</th>
                      <th className="pb-2">{t('chatbotDocs.s7_fWhere')}</th>
                    </tr></thead>
                    <tbody className="divide-y divide-gray-100 text-xs text-gray-700">
                      {[
                        [t('chatbotDocs.s7_fDispName'),    t('chatbotDocs.s7_fDispNameDesc')],
                        [t('chatbotDocs.s7_fPageId'),      t('chatbotDocs.s7_fPageIdDesc')],
                        [t('chatbotDocs.s7_fToken'),       t('chatbotDocs.s7_fTokenDesc')],
                        [t('chatbotDocs.s7_fWebhookToken'),t('chatbotDocs.s7_fWebhookTokenDesc')],
                      ].map(([f, d]) => (
                        <tr key={f}><td className="py-2 pr-4 font-medium text-gray-800">{f}</td><td className="py-2">{d}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <h3 className="font-semibold text-gray-900 mb-3">{t('chatbotDocs.s7_step2Title')}</h3>
                <div className="space-y-1 mb-5">
                  <Step n={1} text={t('chatbotDocs.s7_step2_1')} />
                  <Step n={2} text={t('chatbotDocs.s7_step2_2')} />
                  <Step n={3} text={t('chatbotDocs.s7_step2_3')} />
                  <Step n={4} text={t('chatbotDocs.s7_step2_4')} />
                  <Step n={5} text={t('chatbotDocs.s7_step2_5')} />
                </div>

                <h3 className="font-semibold text-gray-900 mb-3">{t('chatbotDocs.s7_howTitle')}</h3>
                <div className="rounded-xl border border-gray-200 p-5 mb-5 space-y-3 text-sm text-gray-600">
                  {[
                    t('chatbotDocs.s7_how1'),
                    t('chatbotDocs.s7_how2'),
                    t('chatbotDocs.s7_how3'),
                    t('chatbotDocs.s7_how4'),
                    t('chatbotDocs.s7_how5'),
                  ].map((text, i) => (
                    <div key={i} className="flex gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-xs font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
                      <p>{text}</p>
                    </div>
                  ))}
                </div>

                <div className="flex items-start gap-2 p-4 bg-amber-50 rounded-xl border border-amber-200 text-sm text-amber-800">
                  <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-600" />
                  <span>{t('chatbotDocs.s7_billing')}</span>
                </div>
              </section>

              {/* ── 8. Instagram Channel Setup ─────────────────────────── */}
              <section>
                <SectionHeading
                  icon={MessageSquare}
                  id="instagram"
                  title={t('chatbotDocs.s8_title')}
                  subtitle={t('chatbotDocs.s8_subtitle')}
                />

                <p className="text-gray-600 mb-5">
                  {t('chatbotDocs.s8_intro')}
                </p>

                <h3 className="font-semibold text-gray-900 mb-3">{t('chatbotDocs.s8_prereqs')}</h3>
                <div className="rounded-xl border border-gray-200 p-5 mb-6 space-y-2">
                  {[
                    [t('chatbotDocs.s8_p1'), t('chatbotDocs.s8_p1Desc')],
                    [t('chatbotDocs.s8_p2'), t('chatbotDocs.s8_p2Desc')],
                    [t('chatbotDocs.s8_p3'), t('chatbotDocs.s8_p3Desc')],
                    [t('chatbotDocs.s8_p4'), t('chatbotDocs.s8_p4Desc')],
                  ].map(([title, desc]) => (
                    <div key={title} className="flex gap-3 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                      <div><span className="font-medium text-gray-800">{title}</span> — <span className="text-gray-600">{desc}</span></div>
                    </div>
                  ))}
                </div>

                <h3 className="font-semibold text-gray-900 mb-3">{t('chatbotDocs.s8_step1Title')}</h3>
                <div className="space-y-1 mb-4">
                  <Step n={1} text={t('chatbotDocs.s8_step1_1')} />
                  <Step n={2} text={t('chatbotDocs.s8_step1_2')} />
                </div>
                <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 mb-5">
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      <th className="pb-2 pr-4">{t('chatbotDocs.s8_fField')}</th>
                      <th className="pb-2">{t('chatbotDocs.s8_fWhere')}</th>
                    </tr></thead>
                    <tbody className="divide-y divide-gray-100 text-xs text-gray-700">
                      {[
                        [t('chatbotDocs.s8_fDispName'),    t('chatbotDocs.s8_fDispNameDesc')],
                        [t('chatbotDocs.s8_fAccountId'),   t('chatbotDocs.s8_fAccountIdDesc')],
                        [t('chatbotDocs.s8_fToken'),       t('chatbotDocs.s8_fTokenDesc')],
                        [t('chatbotDocs.s8_fWebhookToken'),t('chatbotDocs.s8_fWebhookTokenDesc')],
                      ].map(([f, d]) => (
                        <tr key={f}><td className="py-2 pr-4 font-medium text-gray-800">{f}</td><td className="py-2">{d}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <h3 className="font-semibold text-gray-900 mb-3">{t('chatbotDocs.s8_step2Title')}</h3>
                <div className="space-y-1 mb-5">
                  <Step n={1} text={t('chatbotDocs.s8_step2_1')} />
                  <Step n={2} text={t('chatbotDocs.s8_step2_2')} />
                  <Step n={3} text={t('chatbotDocs.s8_step2_3')} />
                  <Step n={4} text={t('chatbotDocs.s8_step2_4')} />
                  <Step n={5} text={t('chatbotDocs.s8_step2_5')} />
                </div>

                <h3 className="font-semibold text-gray-900 mb-3">{t('chatbotDocs.s8_howTitle')}</h3>
                <div className="rounded-xl border border-gray-200 p-5 mb-5 space-y-3 text-sm text-gray-600">
                  {[
                    t('chatbotDocs.s8_how1'),
                    t('chatbotDocs.s8_how2'),
                    t('chatbotDocs.s8_how3'),
                    t('chatbotDocs.s8_how4'),
                    t('chatbotDocs.s8_how5'),
                  ].map((text, i) => (
                    <div key={i} className="flex gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-100 text-purple-700 text-xs font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
                      <p>{text}</p>
                    </div>
                  ))}
                </div>

                <div className="flex items-start gap-2 p-4 bg-amber-50 rounded-xl border border-amber-200 text-sm text-amber-800">
                  <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-600" />
                  <span>{t('chatbotDocs.s8_note')}</span>
                </div>
              </section>

            </main>
          </div>
        </div>

        {/* Footer CTA */}
        <section className="section-sm bg-gray-50 border-t border-gray-200">
          <div className="container-custom text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-3">{t('chatbotDocs.needHelp')}</h2>
            <p className="text-gray-600 mb-6">{t('chatbotDocs.needHelpSubtitle')}</p>
            <div className="flex gap-4 justify-center">
              <Link to="/docs" className="btn btn-outline">{t('chatbotDocs.backToDocs')}</Link>
              <Link to="/contact" className="btn btn-primary">{t('chatbotDocs.contactSupport')}</Link>
            </div>
          </div>
        </section>

      </div>
    </>
  );
};

export default ChatbotDocsPage;
